import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
      <div className="container-custom py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--brand-blue)] hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 md:p-12 shadow-sm">
          <h1 className="heading-1 mb-8">Terms of Use</h1>
          <p className="body-small text-[var(--text-muted)] mb-8">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8">
            <section>
              <h2 className="heading-2 mb-4">1. Acceptance of Terms</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                By accessing and using the Infiniti Health website and services, you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">2. Description of Services</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                Infiniti Health provides healthcare support services including:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-[var(--text-secondary)] ml-4">
                <li>Cashless desk handling and management for hospitals</li>
                <li>Reimbursement financing support through partnered NBFCs</li>
                <li>Patient reimbursement claim assistance</li>
                <li>Insurance claim processing and coordination</li>
              </ul>
            </section>

            <section>
              <h2 className="heading-2 mb-4">3. User Responsibilities</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-3">
                As a user of our services, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-[var(--text-secondary)] ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not misuse or attempt to gain unauthorized access to our services</li>
                <li>Update your information promptly when changes occur</li>
              </ul>
            </section>

            <section>
              <h2 className="heading-2 mb-4">4. Service Limitations</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                While we strive to provide excellent service, please note:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-[var(--text-secondary)] ml-4">
                <li>Approval of insurance claims and financing is subject to insurer and NBFC policies</li>
                <li>We act as facilitators and do not guarantee claim approval</li>
                <li>Processing times may vary depending on third-party entities</li>
                <li>Service availability may be affected by technical or operational issues</li>
              </ul>
            </section>

            <section>
              <h2 className="heading-2 mb-4">5. Financial Services Disclaimer</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                Reimbursement financing is facilitated through partnered NBFCs. All financing is subject to:
              </p>
              <ul className="list-disc list-inside space-y-2 body-medium text-[var(--text-secondary)] ml-4">
                <li>Policy verification and eligibility assessment</li>
                <li>Hospital-level screening and documentation</li>
                <li>Lender credit checks and approval processes</li>
                <li>Terms and conditions set by the financing partner</li>
              </ul>
            </section>

            <section>
              <h2 className="heading-2 mb-4">6. Intellectual Property</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                All content on this website, including text, graphics, logos, and software, is the property of Infiniti Health or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">7. Confidentiality</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                We maintain strict confidentiality of all medical and financial information provided to us. Our handling of personal data is governed by our Privacy Policy. Users must also maintain confidentiality of sensitive information accessed through our services.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">8. Limitation of Liability</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                To the maximum extent permitted by law, Infiniti Health shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services. Our total liability shall not exceed the amount paid by you for our services.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">9. Indemnification</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                You agree to indemnify and hold harmless Infiniti Health, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from your use of our services or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">10. Termination</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                We reserve the right to terminate or suspend your access to our services at any time, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">11. Governing Law</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">12. Changes to Terms</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website. Your continued use of our services after such changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="heading-2 mb-4">13. Contact Information</h2>
              <p className="body-medium text-[var(--text-secondary)] mb-4">
                For questions about these Terms of Use, please contact us at:
              </p>
              <div className="bg-[var(--accent-blue-200)] rounded-lg p-6 mt-4">
                <p className="body-medium text-[var(--text-primary)]">
                  <strong>Infiniti Health</strong><br />
                  #3 1st Floor, Opp Sahana Vidya Mandir<br />
                  Naganathapura, Bangalore - 560100<br />
                  Email: info@infiniti-health.com<br />
                  Phone: 7676620620
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
